'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Heart, ArrowRight, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

type AuthMode = 'login' | 'signup';
type UserRole = 'recovery' | 'volunteer';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
  terms: boolean;
}

const roleOptions = [
  { value: 'recovery' as UserRole, label: 'Estou em Recuperação', desc: 'Quero acompanhar meu progresso e buscar apoio', icon: Heart, color: 'salvia' },
  { value: 'volunteer' as UserRole, label: 'Sou Voluntário / Apoiador', desc: 'Quero ajudar pessoas na jornada de recuperação', icon: Users, color: 'areia' },
];

export default function AuthForm() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>('recovery');
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signUp } = useAuth();
  const router = useRouter();

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupForm>({ defaultValues: { name: '', email: '', password: '', confirmPassword: '', role: 'recovery', terms: false } });

  const onLogin = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      const role = result?.user?.user_metadata?.role || 'recovery';
      toast.success('Bem-vindo de volta!');

      let destination = '/recovery-tracking-dashboard';

      if (role === 'admin') {
        destination = '/admin-panel';
      } else if (role === 'volunteer') {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        const { data: appData } = await supabase
          .from('volunteer_applications')
          .select('status')
          .eq('user_id', result.user.id)
          .maybeSingle();

        if (!appData) {
          destination = '/seja-voluntario';
        } else if (appData.status === 'approved') {
          destination = '/volunteer-dashboard';
        } else if (appData.status === 'pending') {
          toast.info('Sua candidatura está em análise. Aguarde a aprovação do administrador.');
          destination = '/seja-voluntario?status=pending';
        } else {
          toast.error('Sua candidatura não foi aprovada. Entre em contato com a equipe.');
          destination = '/seja-voluntario?status=rejected';
        }
      }

      // Small delay to ensure session cookies are fully written before navigation
      await new Promise((resolve) => setTimeout(resolve, 300));
      window.location.replace(destination);
    } catch (error: any) {
      loginForm.setError('email', { message: error?.message || 'Credenciais inválidas. Verifique seu e-mail e senha.' });
      setIsLoading(false);
    }
  };

  const onSignup = async (data: SignupForm) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'As senhas não coincidem' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { name: data.name, role: selectedRole });
      toast.success('Conta criada com sucesso! Bem-vindo ao 1 Dia de Cada Vez.');

      let destination = '/recovery-tracking-dashboard';
      if (selectedRole === 'volunteer') {
        destination = '/seja-voluntario';
      }

      // Small delay to ensure session cookies are fully written before navigation
      await new Promise((resolve) => setTimeout(resolve, 300));
      window.location.replace(destination);
    } catch (error: any) {
      signupForm.setError('email', { message: error?.message || 'Erro ao criar conta. Tente novamente.' });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10" style={{ backgroundColor: '#F8F7F4' }}>
      <div className="w-full max-w-md space-y-6 animate-fade-in">

        {/* Logo — centered above the toggle */}
        <div className="flex justify-center">
          <Image
            src="/assets/images/logo_1dia_transparente-1777268210110.png"
            alt="1 Dia de Cada Vez — Plataforma de Recuperação de Vício em Apostas"
            width={220}
            height={110}
            className="object-contain"
            style={{ maxHeight: '110px' }}
          />
        </div>

        {/* Mode Toggle */}
        <div className="rounded-xl p-1 flex" style={{ backgroundColor: '#E8E5DC' }}>
          {(['login', 'signup'] as AuthMode[]).map((m) => (
            <button
              key={`mode-${m}`}
              onClick={() => setMode(m)}
              className="flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-200"
              style={mode === m
                ? { backgroundColor: '#F8F7F4', color: '#1A1A2E', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }
                : { color: '#7C9E87' }
              }
            >
              {m === 'login' ? 'Entrar' : 'Criar Conta'}
            </button>
          ))}
        </div>

        {mode === 'login' ? (
          <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-5">
            <div className="space-y-1">
              <h2 className="text-2xl font-bold font-serif" style={{ color: '#1A1A2E' }}>Bem-vindo de volta</h2>
              <p className="text-sm" style={{ color: '#7C9E87' }}>Entre na sua conta para continuar sua jornada</p>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium" style={{ color: '#1A1A2E' }}>E-mail</label>
              <input
                type="email"
                autoComplete="email"
                placeholder="seu@email.com.br"
                className="w-full px-4 py-2.5 rounded-xl border text-sm transition-all duration-150 focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: '#F8F7F4',
                  borderColor: loginForm.formState.errors.email ? '#ef4444' : '#DDD9CE',
                  color: '#1A1A2E',
                }}
                {...loginForm.register('email', { required: 'E-mail é obrigatório' })}
              />
              {loginForm.formState.errors.email && (
                <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium" style={{ color: '#1A1A2E' }}>Senha</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Sua senha"
                  className="w-full px-4 py-2.5 pr-10 rounded-xl border text-sm transition-all duration-150 focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: '#F8F7F4',
                    borderColor: loginForm.formState.errors.password ? '#ef4444' : '#DDD9CE',
                    color: '#1A1A2E',
                  }}
                  {...loginForm.register('password', { required: 'Senha é obrigatória' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: '#7C9E87' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {loginForm.formState.errors.password && (
                <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border"
                  style={{ accentColor: '#7C9E87' }}
                  {...loginForm.register('remember')}
                />
                <span className="text-sm" style={{ color: '#7C9E87' }}>Lembrar de mim</span>
              </label>
              <button type="button" className="text-sm font-medium hover:opacity-80" style={{ color: '#7C9E87' }}>
                Esqueci a senha
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 font-medium py-3 rounded-xl transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed text-white"
              style={{ backgroundColor: '#7C9E87' }}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>Entrar <ArrowRight size={16} /></>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-5">
            <div className="space-y-1">
              <h2 className="text-2xl font-semibold" style={{ color: '#1A1A2E' }}>Criar sua conta</h2>
              <p className="text-sm" style={{ color: '#7C9E87' }}>Gratuito, confidencial e sem julgamentos</p>
            </div>

            {/* Role Selector */}
            <div className="space-y-2">
              <label className="block text-sm font-medium" style={{ color: '#1A1A2E' }}>Como você quer participar?</label>
              <div className="space-y-2">
                {roleOptions.map((opt) => {
                  const RoleIcon = opt.icon;
                  const isSelected = selectedRole === opt.value;
                  return (
                    <button
                      key={`role-${opt.value}`}
                      type="button"
                      onClick={() => {
                        setSelectedRole(opt.value);
                        signupForm.setValue('role', opt.value);
                      }}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-150"
                      style={{
                        borderColor: isSelected ? (opt.color === 'salvia' ? '#7C9E87' : opt.color === 'areia' ? '#C4A882' : '#64748b') : '#DDD9CE',
                        backgroundColor: isSelected ? (opt.color === 'salvia' ? 'rgba(124,158,135,0.1)' : opt.color === 'areia' ? 'rgba(196,168,130,0.1)' : 'rgba(100,116,139,0.1)') : '#F8F7F4',
                      }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{
                          backgroundColor: isSelected
                            ? (opt.color === 'salvia' ? '#7C9E87' : opt.color === 'areia' ? '#C4A882' : '#64748b')
                            : '#E8E5DC',
                          color: isSelected ? '#fff' : '#7C9E87',
                        }}
                      >
                        <RoleIcon size={18} />
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: '#1A1A2E' }}>{opt.label}</p>
                        <p className="text-xs" style={{ color: '#7C9E87' }}>{opt.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Name */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium" style={{ color: '#1A1A2E' }}>Nome completo</label>
              <input
                type="text"
                placeholder="Seu nome"
                className="w-full px-4 py-2.5 rounded-xl border text-sm transition-all duration-150 focus:outline-none focus:ring-2"
                style={{ backgroundColor: '#F8F7F4', borderColor: signupForm.formState.errors.name ? '#ef4444' : '#DDD9CE', color: '#1A1A2E' }}
                {...signupForm.register('name', { required: 'Nome é obrigatório' })}
              />
              {signupForm.formState.errors.name && (
                <p className="text-xs text-red-500">{signupForm.formState.errors.name.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium" style={{ color: '#1A1A2E' }}>E-mail</label>
              <input
                type="email"
                placeholder="seu@email.com.br"
                className="w-full px-4 py-2.5 rounded-xl border text-sm transition-all duration-150 focus:outline-none focus:ring-2"
                style={{ backgroundColor: '#F8F7F4', borderColor: signupForm.formState.errors.email ? '#ef4444' : '#DDD9CE', color: '#1A1A2E' }}
                {...signupForm.register('email', {
                  required: 'E-mail é obrigatório',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido' },
                })}
              />
              {signupForm.formState.errors.email && (
                <p className="text-xs text-red-500">{signupForm.formState.errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium" style={{ color: '#1A1A2E' }}>Senha</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 8 caracteres"
                    className="w-full px-4 py-2.5 pr-9 rounded-xl border text-sm transition-all duration-150 focus:outline-none focus:ring-2"
                    style={{ backgroundColor: '#F8F7F4', borderColor: signupForm.formState.errors.password ? '#ef4444' : '#DDD9CE', color: '#1A1A2E' }}
                    {...signupForm.register('password', { required: 'Obrigatório', minLength: { value: 8, message: 'Mínimo 8 caracteres' } })}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2" style={{ color: '#7C9E87' }}>
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-xs text-red-500">{signupForm.formState.errors.password.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium" style={{ color: '#1A1A2E' }}>Confirmar</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Repita a senha"
                    className="w-full px-4 py-2.5 pr-9 rounded-xl border text-sm transition-all duration-150 focus:outline-none focus:ring-2"
                    style={{ backgroundColor: '#F8F7F4', borderColor: signupForm.formState.errors.confirmPassword ? '#ef4444' : '#DDD9CE', color: '#1A1A2E' }}
                    {...signupForm.register('confirmPassword', { required: 'Obrigatório' })}
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-2.5 top-1/2 -translate-y-1/2" style={{ color: '#7C9E87' }}>
                    {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-500">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            {/* Terms */}
            <div className="space-y-1.5">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 mt-0.5 rounded border"
                  style={{ accentColor: '#7C9E87' }}
                  {...signupForm.register('terms', { required: 'Você precisa aceitar os termos' })}
                />
                <span className="text-sm" style={{ color: '#7C9E87' }}>
                  Li e aceito os{' '}
                  <button type="button" className="font-medium hover:opacity-80" style={{ color: '#7C9E87' }}>Termos de Uso</button>
                  {' '}e a{' '}
                  <button type="button" className="font-medium hover:opacity-80" style={{ color: '#7C9E87' }}>Política de Privacidade</button>
                </span>
              </label>
              {signupForm.formState.errors.terms && (
                <p className="text-xs text-red-500">{signupForm.formState.errors.terms.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 font-medium py-3 rounded-xl transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed text-white"
              style={{ backgroundColor: '#7C9E87' }}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>Criar minha conta <ArrowRight size={16} /></>
              )}
            </button>
          </form>
        )}

        <p className="text-center text-xs" style={{ color: '#7C9E87' }}>
          Plataforma gratuita · Seus dados são confidenciais
        </p>
      </div>
    </div>
  );
}